import { NewsArticle, NewsArticlePost } from "@infoland/iprova-api";
import { NewsArticleRequest } from "../../../types/newsArticles-request";
import * as Types from "../../../types";
import { ApiProxy, ApiCallResult, PagedResult } from "@infoland/api-proxy";
import snackBarService from "../../../services/snackBarService";
export interface NewsArticleState {
    newsArticle: NewsArticle;
    newsArticlesByPortalId: NewsArticle[];
}
export function initialize(): NewsArticleState {
    return {
        newsArticle: null,
        newsArticlesByPortalId: null
    };
}
// initial state
const state: NewsArticleState = initialize();
// getters for this store
const getters = {
    newsArticle(state: NewsArticleState): NewsArticle {
        return state.newsArticle;
    },
    /**
     * The id of the article
     * @param state The state to read from
     */
    newsArticlesByPortalId(state: NewsArticleState): NewsArticle[] {
        return state.newsArticlesByPortalId;
    }
};
// mutations for this store
const mutations = {
    _newsArticlesByPortalId(state: NewsArticleState, newsArticles: NewsArticle[]): void {
        // undefined should become null
        if (!newsArticles) {
            newsArticles = null;
        }

        state.newsArticlesByPortalId = newsArticles;
    },
    _getNewsArticle(state: NewsArticleState, newsArticle: NewsArticle): void {
        // undefined should become null
        if (!newsArticle) {
            newsArticle = null;
        }
        state.newsArticle = newsArticle;
    }
};
// actions for this store
const actions = {
    //these actions are changed only for testing purpose. It will be reverted
    async loadNewsArticlesByPortal({ commit }, requestModel: NewsArticleRequest): Promise<NewsArticle[]> {
        const limit = null;
        const offset = null;
        const envelope = null;
        let path = `portals/${requestModel.PortalId}/news_articles`;
        path += `${limit ? ApiProxy.setURLParam(path, "limit", limit) : ""}`;
        path += `${offset ? ApiProxy.setURLParam(path, "offset", offset) : ""}`;
        path += `${envelope ? ApiProxy.setURLParam(path, "envelope", envelope) : ""}`;
        path += `${
            requestModel.CreatedOnSince
                ? ApiProxy.setURLParam(path, "created_on_date_time_since", requestModel.CreatedOnSince.toAPIDateTimeString())
                : ""
        }`;
        path += `${requestModel.IncludeContentItems ? ApiProxy.setURLParam(path, "include_content_items", requestModel.IncludeContentItems) : ""}`;
        const response: ApiCallResult<NewsArticle[]> = await ApiProxy.getRequest(snackBarService.setMessage, path);
        if (response.statusCode == 200) {
            const pagedResult = new PagedResult<NewsArticle>(response);
            commit("_newsArticlesByPortalId", pagedResult.items);
            return pagedResult.items;
        } else {
            //if error, commit the list of newsArticles as empty list so the skeleton is not be shown and clean up the already list of news articles
            commit("_newsArticlesByPortalId", []);
            return null;
        }
    },
    async createNewsArticleAsync({ commit }, newsArticleModel: NewsArticlePost): Promise<void | null> {
        const path = `/portals/news_articles`;
        const response: ApiCallResult<void> = await ApiProxy.postRequest(snackBarService.setMessage, path, newsArticleModel);
        if (response.statusCode == 201) return response.result;
        else return null;
    },
    async saveNewsArticleAsync({ commit }, newsArticle): Promise<boolean> {
        const path = `/portals/news_articles/${newsArticle.id}`;
        const response: ApiCallResult<void> = await ApiProxy.patchRequest(snackBarService.setMessage, path, newsArticle.newsArticleProps);
        if (response.statusCode == 204) return true;
        else return false;
    },
    async loadNewsArticleAsync({ commit }, newsArticleProps: Types.NewsArticleGet): Promise<NewsArticle> {
        let path = `portals/news_articles/${newsArticleProps.newsArticleId}`;
        if (newsArticleProps.includePortals) path += "?include_portals=true";

        const response: ApiCallResult<NewsArticle> = await ApiProxy.getRequest<NewsArticle>(snackBarService.setMessage, path);
        if (response.statusCode == 200) {
            commit("_getNewsArticle", response.result);
            return response.result;
        } else return null;
    },
    async deleteNewsArticleAsync({ commit }, newsArticleId: number): Promise<boolean> {
        const path = `/portals/news_articles/${newsArticleId}`;
        const response: ApiCallResult<void> = await ApiProxy.deleteRequest(snackBarService.setMessage, path);
        if (response.statusCode == 204) return true;
        else return false;
    }
};
// export the namespace
export const namespace = "news-article";
// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
